import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { useTheme, useMediaQuery } from "@mui/material";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";

import {
    CheckCircle,
    DangerCircle,
    Pen,
    CloseCircle,
} from "@solar-icons/react/ssr";

import { request } from "@/Api";
import {
    TargetsPass,
    TargetsWarning,
    TargetsError,
} from "@/common/StyledComponents";
import BidDeepCalculate from "@/sheet/BidDeepCalculate";
import * as constants from "@/sheet/constants";
import { store } from "@/sheet/bidStore";
import { formattedCurrency } from "@/utils";
import useEntitlements from "@/auth/useEntitlements";
import { FEATURE_VIEW_BID_GOALS } from "@/constants";

const CustomBox = styled(Box)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => (props.isMobile ? "80%" : "50%")};
    padding: 24px;
    border-radius: 20px;
    background-color: ${(props) => props.backgroundColor};
`;
const Title = styled.h2`
    margin: ${(props) => (props.center ? "24px 0 24px 0" : "0 0 24px 0")};
    font-size: 28px;
    font-weight: normal;
    text-align: ${(props) => (props.center ? "center" : "left")};

    span {
        margin-left: 12px;
        font-size: 14px;
    }
`;
const Subtitle = styled.h3`
    margin: 2px 0 24px 0;
    font-size: 14px;
    font-weight: 600;
`;
const B = styled.span`
    font-weight: 600;
`;
const SubmitNote = styled.p`
    font-size: 14px;
    color: ${(props) => props.color};
    margin: 12px 0 0 0;
`;

export default function BidActionPopup({
    bidId,
    action,
    isFinance,
    open,
    setOpen,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { hasFeature } = useEntitlements();
    const canViewBidGoals = hasFeature(FEATURE_VIEW_BID_GOALS);

    const [deepCalculating, setDeepCalculating] = useState(true);
    const [loading, setLoading] = useState(false);
    const [note, setNote] = useState("");

    const insurance = store.bid.insurance;
    const default_insurance = store.bid.default_insurance;
    const bills = store.bid.bills;
    const total_quoted = store.bid.insights.total_quoted;
    const project_total_quoted = store.bid.insights.project_total_quoted;
    const target_quoted = store.bid.insights.target_quoted;
    const minimum_quoted = store.bid.insights.minimum_quoted;
    const currency = store.bid.project.currency;
    const bidType = store.bid.bid_type;
    const bidTypeName =
        bidType == constants.BID_TYPE_CO ? bidType : `${bidType} Bid Sheet`;

    const financial_targets = [
        {
            pass: project_total_quoted >= minimum_quoted,
            value: project_total_quoted,
            goal: minimum_quoted,
            financeApproval: true,
            failMessage: (
                <>
                    The project's total quoted is{" "}
                    <B>
                        {formattedCurrency(
                            project_total_quoted,
                            currency,
                            true,
                        )}
                    </B>{" "}
                    which is below minumum of{" "}
                    {formattedCurrency(minimum_quoted, currency, true)}.
                </>
            ),
        },
        {
            pass: project_total_quoted >= target_quoted,
            value: project_total_quoted,
            goal: target_quoted,
            financeApproval: false,
            passMessage: (
                <>
                    The project's total quoted is{" "}
                    <B>
                        {formattedCurrency(
                            project_total_quoted,
                            currency,
                            true,
                        )}
                    </B>{" "}
                    which is above target of{" "}
                    {formattedCurrency(target_quoted, currency, true)}.
                </>
            ),
            failMessage: (
                <>
                    The project's total quoted is{" "}
                    <B>
                        {formattedCurrency(
                            project_total_quoted,
                            currency,
                            true,
                        )}
                    </B>{" "}
                    which is below target of{" "}
                    {formattedCurrency(target_quoted, currency, true)}.
                </>
            ),
        },
        {
            pass: insurance >= default_insurance,
            financeApproval: false,
            hidePass: true,
            failMessage:
                "Insurance costs have been modified and are less than expected.",
        },
    ];

    const bill_targets = [
        {
            pass: bills.length > 0,
            financeApproval: false,
            hidePass: true,
            failMessage: "Billing schedule is missing.",
            required: true,
        },
        {
            pass: !bills.some((bill) => !bill.bill_date),
            financeApproval: false,
            hidePass: true,
            failMessage: "Billing schedule has missing dates.",
            required: true,
        },
        {
            pass: !bills.some((bill) => !bill.actual_amount),
            financeApproval: false,
            hidePass: true,
            failMessage: "Billing schedule has missing actuals.",
            required: true,
        },
        {
            pass:
                Math.round(
                    bills.reduce((acc, bill) => acc + bill.actual_amount, 0),
                ) == Math.round(total_quoted),
            financeApproval: false,
            hidePass: true,
            failMessage: "Billing total doesn't match quoted amount.",
            required: true,
        },
        {
            pass: bills.some((bill) => bill.quoted_percentage == 0)
                ? true
                : Math.round(
                      bills.reduce(
                          (total, bill) => total + bill.quoted_percentage * 100,
                          0,
                      ),
                  ) == 100,
            financeApproval: false,
            hidePass: true,
            failMessage: "Billing schedule does not add up to 100%.",
            required: true,
        },
    ];

    const targets = canViewBidGoals
        ? financial_targets.concat(bill_targets)
        : [];

    // Seperate targets into pass/fail
    const pass = targets.filter((x) => x.pass && x.passMessage);
    const fail = targets.filter((x) => !x.pass);
    const warnings = fail.filter((x) => !x.required && !x.financeApproval);
    const errors = fail.filter((x) => x.required && !x.financeApproval);
    const requiredApprovals = fail.filter((x) => x.financeApproval);
    const finanaceApprovalRequired =
        action == constants.BID_ACTIVITY_APPROVED &&
        requiredApprovals.length > 0 &&
        !isFinance;

    useEffect(() => {
        if (action == constants.BID_ACTIVITY_SUBMITTED_EP) {
            setDeepCalculating(true);
        } else {
            setDeepCalculating(false);
        }
    }, [open, action]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleActivity = (action) => () => {
        setLoading(true);
        request
            .post(`/bids/${bidId}/activity`, { action: action, note: note })
            .then((response) => {
                setNote("");
            })
            .finally(() => {
                setOpen(false);
                setLoading(false);
            });
    };

    return (
        <Modal sx={{ overflow: "scroll" }} open={open} onClose={handleClose}>
            {deepCalculating ? (
                <BidDeepCalculate
                    bidId={store.bid.id}
                    open={deepCalculating}
                    setOpen={setDeepCalculating}
                    hidePass
                />
            ) : (
                <CustomBox
                    backgroundColor={theme.palette.background.default}
                    isMobile={isMobile}
                >
                    <Title>
                        {action == constants.BID_ACTIVITY_SUBMITTED_EP
                            ? `Submit to EP for Approval`
                            : action == constants.BID_ACTIVITY_REJECTED
                              ? `Reject ${bidTypeName}`
                              : finanaceApprovalRequired
                                ? `Submit to Finance for Approval`
                                : action == constants.BID_ACTIVITY_APPROVED
                                  ? `Approve ${bidTypeName}`
                                  : `Invalid action`}
                        <span>
                            {formattedCurrency(
                                total_quoted,
                                currency,
                                false,
                                2,
                            )}
                        </span>
                    </Title>

                    {errors.length > 0 ? (
                        <TargetsError>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <DangerCircle />
                                </Grid>
                                <Grid item>
                                    <Subtitle>Required</Subtitle>
                                    {errors.map((x, i) => (
                                        <p key={i}>{x.failMessage}</p>
                                    ))}
                                </Grid>
                            </Grid>
                        </TargetsError>
                    ) : null}

                    {requiredApprovals.length > 0 ? (
                        <TargetsError>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <DangerCircle />
                                </Grid>
                                <Grid item>
                                    <Subtitle>
                                        Requires Finance Approval
                                    </Subtitle>
                                    {requiredApprovals.map((x, i) => (
                                        <p key={i}>{x.failMessage}</p>
                                    ))}
                                    {(action ==
                                        constants.BID_ACTIVITY_SUBMITTED_EP ||
                                        action ==
                                            constants.BID_ACTIVITY_APPROVED) && (
                                        <p>
                                            Please include a brief note about
                                            this project before submitting.
                                        </p>
                                    )}
                                </Grid>
                            </Grid>
                        </TargetsError>
                    ) : null}

                    {warnings.length > 0 ? (
                        <TargetsWarning>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <DangerCircle />
                                </Grid>
                                <Grid item>
                                    <Subtitle>Attention</Subtitle>
                                    {warnings.map((x, i) => (
                                        <p key={i}>{x.failMessage}</p>
                                    ))}
                                    {requiredApprovals.length == 0 &&
                                        (action ==
                                            constants.BID_ACTIVITY_SUBMITTED_EP ||
                                            action ==
                                                constants.BID_ACTIVITY_APPROVED) && (
                                            <p>
                                                Please include a brief note
                                                about this project before
                                                submitting.
                                            </p>
                                        )}
                                </Grid>
                            </Grid>
                        </TargetsWarning>
                    ) : null}

                    {pass.length > 0 ? (
                        <TargetsPass>
                            <Grid container spacing={1}>
                                <Grid item>
                                    <CheckCircle />
                                </Grid>
                                <Grid item>
                                    <Subtitle>Good stuff</Subtitle>
                                    {pass.map((x, i) => (
                                        <p key={i}>{x.passMessage}</p>
                                    ))}
                                </Grid>
                            </Grid>
                        </TargetsPass>
                    ) : null}

                    {action != constants.BID_ACTIVITY_APPROVED ||
                    finanaceApprovalRequired ? (
                        <TextField
                            label={
                                action == constants.BID_ACTIVITY_REJECTED
                                    ? `Include note for producer...`
                                    : `Include note for approver...`
                            }
                            fullWidth
                            multiline
                            rows={3}
                            value={note}
                            onInput={(event) => setNote(event.target.value)}
                        />
                    ) : null}

                    {action == constants.BID_ACTIVITY_SUBMITTED_EP && (
                        <SubmitNote color={theme.palette.text.secondary}>
                            * All bid settings, including Top Sheet will lock
                            upon submitting.
                        </SubmitNote>
                    )}

                    <Grid
                        container
                        spacing={2}
                        alignItems="center"
                        sx={{ marginTop: "12px" }}
                    >
                        <Grid item flexGrow={1}>
                            <Button
                                fullWidth
                                color="secondary"
                                variant="contained"
                                onClick={handleClose}
                                data-testid="bid-action-cancel-button"
                            >
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item flexGrow={1}>
                            {action == constants.BID_ACTIVITY_APPROVED ? (
                                <Button
                                    fullWidth
                                    color="primary"
                                    sx={{ color: "black" }}
                                    variant="contained"
                                    onClick={handleActivity(
                                        constants.BID_ACTIVITY_APPROVED,
                                    )}
                                    startIcon={<CheckCircle />}
                                    disabled={loading || errors.length > 0}
                                    data-testid="bid-action-approve-button"
                                >
                                    Approve
                                </Button>
                            ) : action == constants.BID_ACTIVITY_REJECTED ? (
                                <Button
                                    fullWidth
                                    color="error"
                                    sx={{ color: "black" }}
                                    variant="contained"
                                    onClick={handleActivity(
                                        constants.BID_ACTIVITY_REJECTED,
                                    )}
                                    startIcon={<CloseCircle />}
                                    disabled={loading}
                                    data-testid="bid-action-reject-button"
                                >
                                    Reject
                                </Button>
                            ) : (
                                <Button
                                    fullWidth
                                    color="primary"
                                    sx={{ color: "black" }}
                                    variant="contained"
                                    onClick={handleActivity(
                                        constants.BID_ACTIVITY_SUBMITTED_EP,
                                    )}
                                    startIcon={<Pen />}
                                    disabled={loading || errors.length > 0}
                                    data-testid="bid-action-send-button"
                                >
                                    Lock & Send
                                </Button>
                            )}
                        </Grid>
                    </Grid>
                </CustomBox>
            )}
        </Modal>
    );
}
